import { AdvancedTradeComponent } from "./components/advanced-trade.component";
import { ChangeFromCloseLabelComponent } from "./components/change-from-close-label.component";
import { ChatSettingsPopupComponent } from "./components/chart-settings-popup.component";
import { DrawEMACloudBuySellArrowComponent } from "./components/draw-ema-cloud-buy-sell-arrow.component";
import { DrawExitOrderComponent } from "./components/draw-exit-order.component";
import { DrawLimitAlertsComponent } from "./components/draw-limit-alerts.component";
import { DrawOrderExecutionComponent } from "./components/draw-order-execution.component";
import { DrawTradeOrdersComponent } from "./components/draw-trade-orders.component";
import { DrawingToolsManager } from "./components/drawing-tools/drawing-tools.manager";
import { EarningNotificationComponent } from "./components/earning-notification.component";
import { EMACloudBuySellSummaryPopupComponent } from "./components/ema-cloud-buy-sell-summary-popup.component";
import { HaltedNotificationComponent } from "./components/halted-notification.component";
import { HighChartAnnotationPopupComponent } from "./components/highchart-annotation-popup.component";
import { HistoricalChartButtonComponent } from "./components/historical-chart-button.component";
import { IndicatorManagerPopupComponent } from "./components/indicator-manager-popup.component";
import { IndicatorLastPriceComponent } from "./components/indicators-last-price.component";
import { InformationPopupComponent } from "./components/information-popup.component";
import { LastPriceComponent } from "./components/last-price.component";
import { LoginPopupComponent } from "./components/login-popup.component";
import { MainSymbolCurrentPriceComponent } from "./components/main-symbol-current-price.component";
import { MarketingWebChartSingleStockWindowComponent } from "./components/marketing-webchart-single-stock-window.component";
import { MarketingWebChartSummaryStatusLineComponent } from "./components/marketing-webchart-summary-status-line";
import { OrderRejectionMessageComponent } from "./components/order-rejection-message.component";
import { PnlComponent } from "./components/pnl.component";
import { PnPSwitchComponent } from "./components/pnp-switch.component";
import { ScalingResetButtonComponent } from "./components/scaling-reset-button.component";
import { TiChartSeriesComponent } from "./components/ti-chart-series.component";
import { TILogoComponent } from "./components/ti-logo.component";
import { TooltipComponent } from "./components/tooltip.component";
import { TradeComponent } from "./components/trade.component";
import { ZoomAndScrollButtonComponent } from "./components/zoom-and-scroll-button.component";
import { DomExtensions } from "./extensions/dom.extensions";
import { TraceLoggingHelpers } from "./helpers/trace-logging-helpers";
import { BaseChartContainer } from "./models/base/base-chart.container";
import { WebchartThemeManager } from "./models/chart-style/web-chart-theme.manager";
import { Timeframe } from "./models/enum/timeframe.enum";
import { SingleStockWindowChangeFromCloseManager } from "./models/single-stock-window/single-stock-window-change-from-close.manager";
import { ChartMode, MarketingWebChartWindowWrapper } from "./models/window-wrapper/marketing-webchart-window-wrapper.model";
import { WebChartDataService } from "./services/web-chart-data.service";
import { TiChartMenuContainer } from "./ti-chart-menu.container";
import { WebChartsMarketingPnpContainer } from "./web-charts-marketing-pnp.container";

declare const global: any;

declare const liveData: any;
declare const userName: any;
declare const userPwd: any;
declare const window: any;
declare const $: any;

declare global {
    interface JQuery {
        spectrum(config: any): void;
    }
}

export class WebChartsMarketingContainer extends BaseChartContainer {
    public override chartService: WebChartDataService = null as any;
    public pnpContainer: WebChartsMarketingPnpContainer | null = null;
    public pnpSwitchComponent: PnPSwitchComponent | null = null;
    public loginPopupComponent: LoginPopupComponent | null = null;
    public singleStockWindow: MarketingWebChartSingleStockWindowComponent | null = null;

    private marketingWebChartWindowWrapper: MarketingWebChartWindowWrapper | null;
    private baseElement: HTMLElement;
    private crendentialsCookieKey = 'TIUSER'

    public static idCounter = 0;

    constructor(baseElement: HTMLElement) {
        super();

        if (!baseElement) {
            return;
        }

        this.baseElement = baseElement;

        if (baseElement.id) {
            this.id = baseElement.id;
        } else {
            const attrKey = 'data-tichart-id';
            let userDefineId = baseElement.getAttribute(attrKey);;
            if (!userDefineId && baseElement.parentElement) {
                userDefineId = baseElement.parentElement.getAttribute(attrKey);
            }

            if (userDefineId) {
                this.id = userDefineId;
            } else {
                this.id = 'chart-' + WebChartsMarketingContainer.idCounter;
                WebChartsMarketingContainer.idCounter++;
            }

            baseElement.id = this.id;
        }

        this.element = baseElement?.querySelector<HTMLElement>('.web-charts-marketing');
        if (!this.element) {
            return;
        }

        const wrapperName = 'web-charts-marketing-wrapper';

        const chartWrapper = this.element.closest('.chart-component-main')?.parentElement;
        this.chartWrapper = chartWrapper;

        chartWrapper?.classList.add(wrapperName);

        new DomExtensions(wrapperName);
        this.element.addClass('one');
        this.element.addClass(this.id);

        const basePathElement = this.element.querySelector('span.base-path');
        if (basePathElement) {
            this.basePath = basePathElement.innerHTML;
            basePathElement.remove();
        }

        this.marketingWebChartMode = true;
        this.marketingWebChartWindowWrapper = new MarketingWebChartWindowWrapper(this);

        WebchartThemeManager.getInstance().loadThemeXML(this.basePath, () => {
            this.init();
        });

        this.handleBodyClickPatches();
    }

    public override toggleChartToPreviewMode(preview: boolean) {
        super.toggleChartToPreviewMode(preview);
        if (preview) {
            this.element.closest<HTMLElement>('.chart-component-main')?.addClass('preview');
        } else {
            this.element.closest<HTMLElement>('.chart-component-main')?.removeClass('preview');
        }
    }

    public override getPnPSwitchElement(): HTMLElement {
        return this.element.closest<HTMLElement>('.chart-component-main')?.findFirst('.pnp-switch');
    }

    public handleLogin() {
        if (typeof liveData != "undefined" && liveData == true) {
            this.isLiveData = true;
        }
        
        const chartService = this.chartService as WebChartDataService;
        if (chartService.waitForLogin) {
            chartService.connection.subcribeToLoginEvent(() => {
                if (chartService.connection.isLoggedIn()) {
                    TraceLoggingHelpers.log(`WebChart - connection logged in, loading chart id: ${this.id}...`);
                    chartService.onLoggedIn();
                    this.pnpContainer?.chartService.onLoggedIn();
                }
            });
        } else {
            if (typeof liveData != "undefined" && liveData == true
                && typeof userName != "undefined" && userName != null
                && typeof userPwd != "undefined" && userPwd != null) {
                this.loginPopupComponent.hide();
                chartService.login(userName, userPwd);
                this.pnpContainer?.subscribeLogin();
            } else {
                //TODO: Later use the Demo account once delayed data is working
                //if (!connectionMaster.isLoggedIn())
                //connectionMaster.login("DEMO", "pure-javascript-api", 3, null, false);
                // Display the temporary login popup 
                if (!chartService.useLoginPopup) {
                    const username = 'DEMO';
                    const password = 'pure-javascript-api';
                    this.loginPopupComponent.hide();
                    chartService.login(username, password);
                    this.pnpContainer?.subscribeLogin();
                } else {
                    this.element.findFirst('.logout')?.removeClass('hidden');
                    this.loginPopupComponent.show();
                    this.pnpContainer?.subscribeLogin();
                    this.loginPopupComponent.subscribeLogin((username: string, password: string) => {
                        const storeToCookieValue = JSON.stringify({ username: username, password: password });
                        sessionStorage.setItem(this.crendentialsCookieKey, storeToCookieValue);
                        chartService.login(username, password);
                    });
                }
            }
        }
    }

    private init() {
        this.chartService = new WebChartDataService(this, this.baseElement);

        const chartMenuElement = this.element.findFirst('.chart-menu-box');
        if (chartMenuElement == null) {
            console.error('ERROR: .chart-menu-box element not found, html needs to be udpated')
            return;
        }

        //Note: the menu needs to be created before the chart series, since HighChart Indicators calls IndicatorLastPrice
        this.tiChartMenu = new TiChartMenuContainer(chartMenuElement, this);

        this.createLoginPopupComponent();
        this.createSingleStockWindowChangeFromCloseManager();

        const pnpElement = this.baseElement?.findFirst('.web-charts-marketing-pnp');

        const chartService = this.chartService as WebChartDataService;
        if (chartService.pnpEnabled && chartService.chartMode == ChartMode.Standard) {
            this.pnpContainer = new WebChartsMarketingPnpContainer(pnpElement, this.baseElement, this);
        } else {
            pnpElement.remove();
        }

        this.handleLogin();

        this.marketingWebChartWindowWrapper?.onChartReady();

        if (chartService.chartMode == ChartMode.Preview) {
            this.createTIChartSeriesComponent();
            this.createMarketingStatusLineComponent();
            this.createLastPriceComponent();
        } else if (chartService.chartMode == ChartMode.PopupChart) {
            this.createTIChartSeriesComponent();
            // this.createMarketingStatusLineComponent();
            this.createLastPriceComponent();
            // this.createMainSymbolCurrentPriceComponent();
            this.createTILogoComponent();
            // this.createHistoricalChartButtonComponent();
            this.createMarketingWebChartSingleStockWindowComponent();
            this.subscribeChartRequestingNewSymbol();
        } else {
            if (!this.isTouchDevice) {
                this.drawingToolsManager = new DrawingToolsManager(this);
            }

            this.createInformationPopupComponent();
            this.createEMACloudBuySellSummaryPopupComponent();
            this.createIndicatorManagerPopupComponent();
            this.createChangeFromCloseLabelComponent();
            this.subscribeChartRequestingNewSymbol();
            this.createTIChartSeriesComponent();

            if (!this.isTouchDevice) {
                this.drawingToolsManager.subscribeForPopupChange();
            }

            this.createHighChartAnnotationPopupComponent();
            this.createLimitAlertsComponent();
            this.createTradeComponent();
            this.createAdvancedTradeComponent();
            this.createDrawEMACloudBuySellArrowComponent();
            this.createDrawExitOrderComponent();
            this.createNotificationMessageComponent();
            this.createTradePlotLinesComponent();
            this.createPnlComponent();
            this.createDrawOrderExecutionComponent();
            this.createIndicatorsLastPriceComponent();
            this.createLastPriceComponent();
            this.createMainSymbolCurrentPriceComponent();
            this.createTILogoComponent();
            this.createHistoricalChartButtonComponent();
            this.createScalingResetButtonComponent();
            this.createZoomAndScrollButtonComponent();
            this.createMarketingStatusLineComponent();
            this.createMarketingWebChartSingleStockWindowComponent();
            this.createHaltedNotificationComponent();
            this.createEarningNotificationComponent();
            this.createTooltipComponent();
            this.createPnPSwitchComponent();
            this.createChartSettingsPopupComponent();
            this.onLogoutClick();
        }
    }

    private createTIChartSeriesComponent() {
        this.tiChartSeries = new TiChartSeriesComponent(this.element, this);
        this.components.push(this.tiChartSeries);

        const startCalc = () => {      
            this.setCurrentFirstAndLastPointChangesFromCloseColor(this.chartService.lastPrice);
        };

        this.tiChartSeries.subscribeChartLoad(this.id, () => {
            startCalc();

            setTimeout(() => {
                this.tiChartSeries?.afterResize();
            }, 1000);
        });

        this.tiChartSeries.subscribeExtremeChange(this.id, () => {
            if (this.chartService.chartMode == ChartMode.Standard) {
                startCalc();
            }
        });

        this.tiChartSeries.subscribeRedrawChange(this.id, () => {
            if (this.chartService.chartMode == ChartMode.Standard) {
                startCalc();
            }
        });
    }

    private createInformationPopupComponent() {
        this.components.push(new InformationPopupComponent(this));
    }

    private createEMACloudBuySellSummaryPopupComponent() {
        this.components.push(new EMACloudBuySellSummaryPopupComponent(this));
    }

    private createIndicatorManagerPopupComponent() {
        this.components.push(new IndicatorManagerPopupComponent(this));
    }

    private createSingleStockWindowChangeFromCloseManager() {
        this.components.push(new SingleStockWindowChangeFromCloseManager(this));
    }
    
    private createChangeFromCloseLabelComponent() {
        this.components.push(new ChangeFromCloseLabelComponent(this));
    }

    private createLastPriceComponent() {
        this.element.querySelectorAll<HTMLElement>('.main-symbol-last-price').forEach((el: HTMLElement) => {
            this.components.push(new LastPriceComponent(el, this));
        });
    }

    private createMainSymbolCurrentPriceComponent() {
        this.element.querySelectorAll<HTMLElement>('.main-symbol-current-price').forEach((el: HTMLElement) => {
            this.components.push(new MainSymbolCurrentPriceComponent(el, this));
        });
    }

    private createPnlComponent() {
        this.element.querySelectorAll<HTMLElement>('.pnl-container').forEach((el: HTMLElement) => {
            this.components.push(new PnlComponent(el, this));
        });
    }

    private createDrawEMACloudBuySellArrowComponent() {
        this.components.push(new DrawEMACloudBuySellArrowComponent(this));
    }

    private createTradePlotLinesComponent() {
        this.components.push(new DrawTradeOrdersComponent(this));
    }

    private createDrawExitOrderComponent() {
        this.components.push(new DrawExitOrderComponent(this));
    }

    private createNotificationMessageComponent() {
        this.components.push(new OrderRejectionMessageComponent(this));
    }
    
    private createDrawOrderExecutionComponent() {
        this.components.push(new DrawOrderExecutionComponent(this));
    }

    private createTradeComponent() {
        this.components.push(new TradeComponent(this));
    }

    private createAdvancedTradeComponent() {
        this.components.push(new AdvancedTradeComponent(this));
    }

    private createHighChartAnnotationPopupComponent() {
        this.components.push(new HighChartAnnotationPopupComponent(this));
    }

    private createIndicatorsLastPriceComponent() {
        this.element.querySelectorAll<HTMLElement>('.indicator-last-prices').forEach((el: HTMLElement) => {
            this.components.push(new IndicatorLastPriceComponent(el, this));
        });
    }

    private createTILogoComponent() {
        this.element.querySelectorAll<HTMLElement>('.logo-wrapper').forEach((el: HTMLElement) => {
            this.components.push(new TILogoComponent(el, this));
        });
    }

    private createHistoricalChartButtonComponent() {
        this.element.querySelectorAll<HTMLElement>('.historical-chart-button').forEach((el: HTMLElement) => {
            this.components.push(new HistoricalChartButtonComponent(el, this));
        });
    }

    private createScalingResetButtonComponent() {
        this.element.querySelectorAll<HTMLElement>('.scaling-reset-button').forEach((el: HTMLElement) => {
            this.components.push(new ScalingResetButtonComponent(el, this));
        });
    }

    private createZoomAndScrollButtonComponent() {
        this.components.push(new ZoomAndScrollButtonComponent(this));
    }

    private createMarketingStatusLineComponent() {
        this.element.querySelectorAll<HTMLElement>('.marketing-status-line').forEach((el: HTMLElement) => {
            this.components.push(new MarketingWebChartSummaryStatusLineComponent(el, this));
        });
    }

    private createMarketingWebChartSingleStockWindowComponent() {
        this.element.querySelectorAll<HTMLElement>('.marketing-webchart-single-stock-window').forEach((el: HTMLElement) => {
            this.singleStockWindow = new MarketingWebChartSingleStockWindowComponent(el, this);

            this.components.push(this.singleStockWindow);
        });
    }

    private createHaltedNotificationComponent() {
        this.element.querySelectorAll<HTMLElement>('.halted-notification-container').forEach((el: HTMLElement) => {
            this.components.push(new HaltedNotificationComponent(el, this));
        });
    }
    
    private createEarningNotificationComponent() {
        this.element.querySelectorAll<HTMLElement>('.earning-notification-container').forEach((el: HTMLElement) => {
            this.components.push(new EarningNotificationComponent(el, this));
        });
    }

    private createTooltipComponent() {
        this.element.querySelectorAll<HTMLElement>('.tooltip').forEach((el: HTMLElement) => {
            this.components.push(new TooltipComponent(el, this));
        });
    }

    private createPnPSwitchComponent() {
        this.element.findFirst('.pnp-switch')?.ifExistThen((el: HTMLElement) => {
            this.pnpSwitchComponent = new PnPSwitchComponent(el, this);
            this.components.push(this.pnpSwitchComponent);
        });
    }

    private createLoginPopupComponent() {
        this.element.findFirst('.popup-login')?.ifExistThen((el: HTMLElement) => {
            this.loginPopupComponent = new LoginPopupComponent(el);
            this.components.push(this.loginPopupComponent);
        });
    }

    private createChartSettingsPopupComponent() {
        this.element.findFirst('.chart-settings-btn')?.ifExistThen((el: HTMLElement) => {
            this.components.push(new ChatSettingsPopupComponent(el, this));
        });
    }

    private onLogoutClick() {
        this.element.findFirst('.logout')?.addEventListener('click', () => {
            const chartService = this.chartService as WebChartDataService;
            chartService.logout();
            sessionStorage.setItem(this.crendentialsCookieKey, '');

            this.handleLogin();
        });
    }

    private createLimitAlertsComponent() {
        this.components.push(new DrawLimitAlertsComponent(this));
    }

    private subscribeChartRequestingNewSymbol() {
        this.chartService.subscribeChartRequestingNewSymbolData((symbol: string, timeframe: Timeframe) => {
            this.drawingToolsManager?.clearAll();
            this.tiChartSeries?.resetExtreamSetFlags();

            this.setComponentStateWhenRequestingNewSymbol(symbol, timeframe);
        });
    }

    public override setComponentStateWhenRequestingNewSymbol(symbol: string, timeframe: Timeframe) {
        const drawOrderExecutionComponent = this.getComponentsByType(DrawOrderExecutionComponent)[0] as DrawOrderExecutionComponent;
        drawOrderExecutionComponent?.remove();

        const drawTradeOrdersComponent = this.getComponentsByType(DrawTradeOrdersComponent)[0] as DrawTradeOrdersComponent;
        drawTradeOrdersComponent?.remove();

        const drawEMACloudBuySellArrowComponent = this.getComponentsByType(DrawEMACloudBuySellArrowComponent)[0] as DrawEMACloudBuySellArrowComponent;
        drawEMACloudBuySellArrowComponent?.disable();

        const eMACloudBuySellSummaryPopupComponent = this.getComponentsByType(EMACloudBuySellSummaryPopupComponent)[0] as EMACloudBuySellSummaryPopupComponent;
        eMACloudBuySellSummaryPopupComponent?.close(true);

        const changeFromCloseLabelComponent = this.getComponentsByType(ChangeFromCloseLabelComponent)[0] as ChangeFromCloseLabelComponent;
        changeFromCloseLabelComponent?.hide();
        
        if (this.tiChartSeries && this.tiChartSeries.symbol == symbol) {
            this.resetTiChartSeries(timeframe, symbol);

            return;
        }

        const haltedNotificationComponent = this.getComponentsByType(HaltedNotificationComponent)[0] as HaltedNotificationComponent;
        haltedNotificationComponent?.hide();

        const earningNotificationComponent = this.getComponentsByType(EarningNotificationComponent)[0] as EarningNotificationComponent;
        earningNotificationComponent?.hide();

        const pnlComponent = this.getComponentsByType(PnlComponent)[0] as PnlComponent;
        pnlComponent?.hideElements();

        const drawLimitAlertsComponent = this.getComponentsByType(DrawLimitAlertsComponent)[0] as DrawLimitAlertsComponent;
        drawLimitAlertsComponent?.remove();

        const infoPopupComponent = this.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
        infoPopupComponent?.close();

        this.element.classList.add('one');
        this.resetTiChartSeries(timeframe, symbol);
        
        const lastPriceComponent = this.getComponentsByType(LastPriceComponent)[0] as LastPriceComponent;
        lastPriceComponent?.hide();

        const indicatorLastPriceComponent = this.getComponentsByType(IndicatorLastPriceComponent)[0] as IndicatorLastPriceComponent;
        indicatorLastPriceComponent?.hide();

        const mainSymbolCurrentPriceComponent = this.getComponentsByType(MainSymbolCurrentPriceComponent)[0] as MainSymbolCurrentPriceComponent;
        mainSymbolCurrentPriceComponent?.hideMenuItems();
        mainSymbolCurrentPriceComponent?.hide();
    }

    private resetTiChartSeries(timeframe: Timeframe, symbol: string) {
        if (this.tiChartSeries) {
            this.tiChartSeries.currentTimeframe = timeframe;
            this.tiChartSeries.symbol = symbol;
            this.tiChartSeries.destroyHighCharts();
            // this.tiChartSeries.removeEMAClouldSeries();
            this.tiChartSeries.setPanel();
            this.tiChartSeries.hideWithOpacity();
            this.tiChartSeries.highChartAxisBuilder?.reset();
            this.tiChartSeries.tiChartPoints = [];
            this.tiChartSeries.lastDataPoint = null;
        }
    }

    private handleBodyClickPatches() {
        this.domEvents.onScreenClick(this.id, () => {
            document.body.querySelectorAll<HTMLElement>('[data-bs-toggle]').forEach((el: HTMLElement) => {
                if (el.style.display == 'none') {
                    el.style.display = '';
                }
            });
            document.body.querySelectorAll<HTMLElement>('[data-bs-parent]').forEach((el: HTMLElement) => {
                if (el.style.display == 'none') {
                    el.style.display = '';
                }
            });
        });
    }
}

function initCharts() {
    document.querySelectorAll<HTMLElement>('.web-charts-marketing.chart-component-main').forEach((el: HTMLElement) => {
        if (!el.classList.contains('inited')) {
            el.classList.add('inited');
            TraceLoggingHelpers.log('chart inited ', el)
            new WebChartsMarketingContainer(el);
        }
    });

    const metaViewPort = document.querySelector('meta[name="viewport"]');
    if (metaViewPort) {
        metaViewPort.setAttribute('content', `width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, shrink-to-fit=no`)
    }
}

if (!window.allChartsInitialized) {
    TraceLoggingHelpers.log('allChartsInitialized', document.readyState);
    if (document.readyState != 'loading') {
        initCharts();
    } else {
        document.addEventListener('DOMContentLoaded', () => {
            initCharts();
        });
    }

    window.allChartsInitialized = true;
}